import { useQueryClient } from '@tanstack/react-query'
import { CopyIcon, EllipsisVerticalIcon, ForwardIcon, LoaderCircleIcon, RotateCwIcon } from 'lucide-react'
import { useEffect, useState } from 'react'
import { toast } from 'sonner'

import { SkeletonItem } from 'components/reusable/SkeletonItem'
import { publicQuotes } from 'rails_routes'
import { Badge, Banner, Button, DropdownMenu, IconButton, Modal, TextButton, Tooltip } from 'slab'
import { cn, getFormattedDate } from 'utils'
import {
  useGetQuotingQuoteResultsQuoteIdResourceAccessTokenRecipients as getInvitedRecipients,
  usePostQuotingQuoteResultsQuoteIdInvite as useShareQuote,
  usePostResourceAccessTokensIdCopy as useCopyLink,
} from 'utils/web_api'
import {
  type ResourceAccessTokensResourceAccessTokenAbilitiesItem,
  type ResourceAccessTokensResourceAccessTokenRecipient as ResourceAccessTokenRecipient,
  ResourceAccessTokensResourceAccessTokenStatus as ResourceAccessTokenStatus,
} from 'utils/web_api.schemas'

import { ShareQuoteForm } from './ShareQuoteForm'

export type StagedInvite = {
  email: string,
  abilities: ResourceAccessTokensResourceAccessTokenAbilitiesItem[]
}

const TokenRecipientRow = (
  { recipient, onResendInvite, onCopyLink, isInviting, isCopyingLink, canEditQuote }:
  { recipient: ResourceAccessTokenRecipient, onResendInvite: (abilities: ResourceAccessTokensResourceAccessTokenAbilitiesItem[]) => void, onCopyLink: (tokenId: string | null, recipient: ResourceAccessTokenRecipient) => void, isInviting: boolean, isCopyingLink: boolean, canEditQuote: boolean }
) => {
  if (recipient.tokens.length === 0) {
    // Should never happen
    return null
  }
  // Recipient can have multiple tokens, if they have a redeemed token we consider that the recipient has accepted the invitation
  const redeemedToken = recipient.tokens.find(token => token.status === ResourceAccessTokenStatus.redeemed)
  const mostRecentRedeemableToken = recipient.tokens.filter(token =>
    token.status === ResourceAccessTokenStatus.unredeemed
  ).sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())[0]

  // we collect all abilities from valid tokens in order to determine the abilities to grant when resending an invite
  // and also to display the cumulative permissions the recipient has
  const cumulativeAbilities = Array.from(
    new Set(
      recipient.tokens
        .filter(token => token.status === ResourceAccessTokenStatus.redeemed || token.status === ResourceAccessTokenStatus.unredeemed)
        .flatMap(token => token.abilities)
    )
  )

  // Display cumulative abilities from all valid tokens, not just one token
  const abilitiesToDisplay = cumulativeAbilities

  const tokenToCopy = mostRecentRedeemableToken || redeemedToken

  return (
    <div key={recipient.id} className='flex items-center justify-between px-2 py-4 hover:bg-gray-50 transition-colors'>
      <div className='font-semibold text-sm flex items-center space-x-2'>
        <span>{recipient.email}</span>
        {isInviting && <LoaderCircleIcon className='w-4 h-4 animate-spin' />}
      </div>
      <div className='flex items-center space-x-2 cursor-default'>
        {abilitiesToDisplay.length > 0 && (
          <Tooltip content={`${recipient.email} has permission to ${abilitiesToDisplay.includes('edit') ? 'edit' : 'view'} the quote${abilitiesToDisplay.includes('onboard') ? ' and onboard to Thatch from it' : ''}.`} withPortal={false}>
            <div className='text-sm text-gray-500 whitespace-nowrap'>
              {abilitiesToDisplay.includes('edit') ? 'Editor' : 'Viewer'}
              {abilitiesToDisplay.includes('onboard') && ', Can onboard'}
            </div>
          </Tooltip>
        )}
        {redeemedToken && redeemedToken.redeemed_at && (
          <Tooltip content={`Accepted invitation on ${getFormattedDate(redeemedToken.redeemed_at)}${redeemedToken.redeemed_by_geo_location ? ` in ${redeemedToken.redeemed_by_geo_location}` : ''}.`} withPortal={false}>
            <Badge type='success'>
              Reviewing quote
            </Badge>
          </Tooltip>
        )}
        {!redeemedToken && mostRecentRedeemableToken && (
          <Tooltip content={`Waiting to accept their invitation sent on ${getFormattedDate(mostRecentRedeemableToken.created_at)}.`} withPortal={false}>
            <Badge type='info'>
              Pending
            </Badge>
          </Tooltip>
        )}
        {!redeemedToken && !mostRecentRedeemableToken && (
          // If they don't have a redeemed token or a redeemable token, they are either expired or revoked
          <Badge type='critical'>
            Expired
          </Badge>
        )}
        {canEditQuote && (
          <IconButton
            icon={isCopyingLink ? LoaderCircleIcon : CopyIcon}
            label='Copy link'
            onClick={() => onCopyLink(tokenToCopy?.id ?? null, recipient)}
            disabled={isCopyingLink}
          />
        )}
        <DropdownMenu
          trigger={<IconButton icon={EllipsisVerticalIcon} label='More' />}
          withPortal={false}
        >
          <DropdownMenu.Item
            leftIcon={RotateCwIcon}
            onSelect={() => onResendInvite(cumulativeAbilities)}
          >
            Resend invite
          </DropdownMenu.Item>
        </DropdownMenu>
      </div>
    </div>
  )
}

export const ShareQuoteButtonModal = ({
  quoteId,
  buttonVariant = 'secondary',
  prospectName,
  hasUnsavedChanges,
  onSaveChanges,
  onDiscardChanges,
  isSavingChanges,
  canSendEditorInvites,
  canSendOnboardInvites,
  canEditQuote,
}: {
  quoteId: string,
  buttonVariant: 'primary' | 'secondary',
  prospectName: string,
  hasUnsavedChanges: boolean,
  onSaveChanges: () => void,
  onDiscardChanges: () => void,
  isSavingChanges: boolean,
  canSendEditorInvites: boolean,
  canSendOnboardInvites: boolean,
  canEditQuote: boolean,
}) => {
  const [shareModalIsOpen, setShareModalIsOpen] = useState(false)
  const [successMessageDetails, setSuccessMessageDetails] = useState<{ fromEmail: string, toEmail: string } | null>(null)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [shakeSaveChangesBanner] = useState(false)
  const [invitingEmail, setInvitingEmail] = useState<string | null>(null)
  const [copyingLinkForEmail, setCopyingLinkForEmail] = useState<string | null>(null)
  const [currentTab, setCurrentTab] = useState<'share' | 'recipients'>('share')
  const [stagedInvites, setStagedInvites] = useState<StagedInvite[]>([])

  const queryClient = useQueryClient()
  const shareQuoteMutation = useShareQuote()
  const copyLinkMutation = useCopyLink()

  const {
    data: invitedRecipients,
    isPending: isInvitedRecipientsPending,
    queryKey: invitedRecipientsQueryKey,
  } = getInvitedRecipients(quoteId)

  const updateQuoteRecipients = (invites: StagedInvite[]) => {
    if (invites.length === 0) return
    shareQuoteMutation.mutate(
      { quoteId, data: { recipients: invites } },
      {
        onSuccess: (data) => {
          setSuccessMessageDetails({ fromEmail: data[0].from_email, toEmail: data.map(r => r.recipient.email).join(', ') })
          queryClient.setQueryData(invitedRecipientsQueryKey, prev => {
            const prevRecipients = prev ?? []
            return data.reduce((updatedRecipients, { recipient: invitedRecipient }) => {
              const existingIndex = updatedRecipients.findIndex(r => r.id === invitedRecipient.id)

              if (existingIndex !== -1) {
                // Replace existing recipient
                return updatedRecipients.map((recipient, index) =>
                  index === existingIndex ? { ...invitedRecipient } : recipient
                )
              } else {
                // Add new recipient to the beginning of the list
                return [invitedRecipient, ...updatedRecipients]
              }
            }, prevRecipients)
          })
          setStagedInvites([])
          setInvitingEmail(null)
        },
        onError: () => {
          setErrorMessage('Error sharing quote, please try again or contact support.')
          setInvitingEmail(null)
        }
      }
    )
  }

  const handleCopyLink = (tokenId: string | null, recipient: ResourceAccessTokenRecipient) => {
    setCopyingLinkForEmail(recipient.email)

    if (!tokenId) {
      setCopyingLinkForEmail(null)
      toast.error('Failed to copy link, please try resending invite email.')
      return
    }
    copyLinkMutation.mutate(
      { id: tokenId },
      {
        onSuccess: (accessToken) => {
          const returnedTokenId = accessToken.id
          if (!returnedTokenId) {
            setCopyingLinkForEmail(null)
            toast.error('Failed to get token ID')
            return
          }
          const path = publicQuotes.show({ id: quoteId, query: { token: returnedTokenId } })
          const url = `${window.location.origin}${path}`
          navigator.clipboard.writeText(url).then(() => {
            setCopyingLinkForEmail(null)
            setShareModalIsOpen(false)
            toast.success('Link copied to clipboard')
          }).catch(() => {
            setCopyingLinkForEmail(null)
            setShareModalIsOpen(false)
            toast.error('Failed to copy link to clipboard')
          })
        },
        onError: () => {
          setCopyingLinkForEmail(null)
          toast.error('Failed to generate link, please try again')
        }
      }
    )
  }

  const closeModal = () => {
    setShareModalIsOpen(false)
    setTimeout(() => {
      setStagedInvites([])
      setSuccessMessageDetails(null)
      setErrorMessage(null)
    }, 500)
  }

  useEffect(() => {
    const commandEnterHandler = (e: KeyboardEvent) => {
      if (e.key === 'Enter' && e.metaKey && shareModalIsOpen && currentTab === 'share') {
        updateQuoteRecipients(stagedInvites)
      }
    }
    if (shareModalIsOpen) {
      document.addEventListener('keydown', commandEnterHandler)
    } else {
      document.removeEventListener('keydown', commandEnterHandler)
    }
    return () => document.removeEventListener('keydown', commandEnterHandler)
  }, [shareModalIsOpen, currentTab, updateQuoteRecipients, stagedInvites])

  return (
    <>
      <Modal open={shareModalIsOpen} onOpenChange={closeModal} width='medium'>
        <Modal.Body>
          <Modal.Title>Share allowance strategy</Modal.Title>
          <div className='flex items-center gap-2 border-b border-gray-200 mb-2'>
            <div
              className={cn(
                'px-4 py-2 cursor-pointer',
                { 'border-b-2 border-gray-700': currentTab === 'share' },
                { 'hover:border-b border-gray-500': currentTab !== 'share' }
              )}
              onClick={() => setCurrentTab('share')}
            >
              Invite others
            </div>
            <div
              className={cn(
                'px-4 py-2 cursor-pointer',
                { 'border-b-2 border-gray-700': currentTab === 'recipients' },
                { 'hover:border-b border-gray-500': currentTab !== 'recipients' }
              )}
              onClick={() => setCurrentTab('recipients')}
            >
              People with access
            </div>
          </div>
          <div>
            {currentTab === 'share' && (
              <div className='space-y-2'>
                {successMessageDetails && (
                  <Banner type='success' title='Quote shared' onDismiss={() => setSuccessMessageDetails(null)}>
                    <p className='text-sm text-green-500'>
                      A link to view the quote has been sent to <span className='font-semibold italic'>{successMessageDetails.toEmail}</span>.
                    </p>
                    <p className='text-sm text-green-500'>
                      They should look for an email from <span className='font-semibold italic'>{successMessageDetails.fromEmail}</span> with the subject line <span className='font-bold italic'>"Review your new Thatch quote for {prospectName}!"</span>.
                    </p>
                  </Banner>
                )}
                {errorMessage && (
                  <Banner type='critical' title={errorMessage} onDismiss={() => setErrorMessage(null)} />
                )}
                {hasUnsavedChanges && (
                  <div className={cn('transition-transform origin-top duration-500', shakeSaveChangesBanner ? 'animate-shake' : '')}>
                    <Banner
                      type='caution'
                      title='Unsaved changes'
                      cta={
                        <div className='space-x-4'>
                          <Button variant='secondary' onClick={onSaveChanges} disabled={isSavingChanges}>
                            Save changes
                          </Button>
                          <TextButton
                            color='gray'
                            onClick={onDiscardChanges}
                          >
                            Discard changes
                          </TextButton>
                        </div>
                      }
                    >
                      <p className='text-sm text-gray-500'>
                        The quote has unsaved changes, you must save or discard them before sharing.
                      </p>
                    </Banner>
                  </div>
                )}
                <ShareQuoteForm
                  onCancelClick={closeModal}
                  canSendEditorInvites={canSendEditorInvites}
                  canSendOnboardInvites={canSendOnboardInvites}
                  stagedInvites={stagedInvites}
                  onStagedInvitesChanged={setStagedInvites}
                  onSendStagedInvites={() => updateQuoteRecipients(stagedInvites)}
                  isSendingInvites={shareQuoteMutation.isPending}
                />
              </div>
            )}
            {currentTab === 'recipients' && (
              <>
                {isInvitedRecipientsPending && <SkeletonItem heightClass='h-44' />}
                {!isInvitedRecipientsPending && invitedRecipients && (
                  invitedRecipients.length > 0
                    ? (
                      <>
                        <div className='max-h-72 overflow-y-auto'>
                          {invitedRecipients.map((recipient) => (
                            <TokenRecipientRow
                              key={recipient.id}
                              recipient={recipient}
                              isInviting={invitingEmail === recipient.email}
                              isCopyingLink={copyingLinkForEmail === recipient.email}
                              onResendInvite={abilities => {
                                setInvitingEmail(recipient.email)
                                updateQuoteRecipients([{ email: recipient.email, abilities }])
                              }}
                              onCopyLink={tokenId => {
                                handleCopyLink(tokenId, recipient)
                              }}
                              canEditQuote={canEditQuote}
                            />
                          ))}
                        </div>
                      </>
                      )
                    : (
                      <div className='text-md flex items-center justify-center h-44'>
                        No one has been invited yet.
                      </div>
                      )
                )}
              </>
            )}
          </div>
        </Modal.Body>
      </Modal>
      <Button
        variant={buttonVariant}
        onClick={() => setShareModalIsOpen(true)}
        disabled={shareQuoteMutation.isPending}
        leftIcon={ForwardIcon}
      >
        Share quote
      </Button>
    </>
  )
}
